import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';

export type ContentMode = 'hydra' | 'custom';

type DbContentMode = 'cms' | 'template';

const dbToApp = (raw: string | null | undefined): ContentMode => {
  if (raw === 'template') return 'custom';
  return 'hydra';
};

const appToDb = (mode: ContentMode): DbContentMode => {
  return mode === 'custom' ? 'template' : 'cms';
};

export const useContentMode = () => {
  const queryClient = useQueryClient();

  const { data: contentMode = 'hydra' as ContentMode, isLoading } = useQuery({
    queryKey: ['content-mode'],
    queryFn: async (): Promise<ContentMode> => {
      const { data, error } = await pmsFrom('business_settings')
        .select('content_mode')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !data) return 'hydra';
      return dbToApp(data.content_mode);
    },
    staleTime: 1000 * 60 * 5,
  });

  const setContentMode = useMutation({
    mutationFn: async (mode: ContentMode) => {
      const dbValue = appToDb(mode);
      const { data: existing } = await pmsFrom('business_settings')
        .select('id')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing) {
        const { error } = await pmsFrom('business_settings')
          .update({ content_mode: dbValue })
          .eq('id', existing.id);
        if (error) throw error;
      }
      return mode;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-mode'] });
      queryClient.invalidateQueries({ queryKey: ['site-content-public'] });
      queryClient.invalidateQueries({ queryKey: ['display-settings'] });
    },
  });

  const isHydraMode = contentMode === 'hydra';
  const isCustomMode = contentMode === 'custom';
  const isCmsMode = isHydraMode;
  const isTemplateMode = false;

  return { contentMode, isHydraMode, isCustomMode, isCmsMode, isTemplateMode, isLoading, setContentMode };
};
